import { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { axiosInstance } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Users as UsersIcon, Shield, Edit, Eye, EyeOff, Clock, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { confirmAsync } from '@/lib/confirm';

// Los cinco permisos que el backend comprueba de verdad en require_permission().
// Esta lista es la unica fuente: la usan el alta, la edicion y los botones de
// marcar todo / ninguno. Antes habia dos listas distintas en esta misma
// pantalla (la tabla usaba 'facturacion', el dialogo usaba 'facturas') y los
// permisos que se marcaban en el dialogo no protegian nada.
//
// 'abre' es lo que el usuario vera en el menu al marcarlo. Si algun dia se
// separan mas fino (Productos aparte de Facturas), hay que partir tambien el
// require_permission() del backend: si no, vuelve el mismo problema.
const PERMISOS = [
  {
    clave: 'facturacion',
    icono: '🧾',
    etiqueta: 'Facturación',
    abre: 'Productos, Presupuestos, Facturas y Remesas SEPA',
  },
  {
    clave: 'crm',
    icono: '👥',
    etiqueta: 'CRM y Clientes',
    abre: 'Clientes, CRM, Campañas y Calendario',
  },
  {
    clave: 'ticketing',
    icono: '🎫',
    etiqueta: 'Ticketing',
    abre: 'Tickets de soporte',
  },
  {
    clave: 'teamup',
    icono: '📆',
    etiqueta: 'Teamup',
    abre: 'Calendario compartido de Teamup',
  },
  {
    clave: 'plantillas_email',
    icono: '📧',
    etiqueta: 'Plantillas de email',
    abre: 'Plantillas de envío',
  },
];

const permisosCon = (valor) =>
  Object.fromEntries(PERMISOS.map(({ clave }) => [clave, valor]));

const permisosDe = (usuario) =>
  Object.fromEntries(
    PERMISOS.map(({ clave }) => [clave, usuario?.permissions?.[clave] === true])
  );

// El Dashboard solo pinta contenido si el usuario tiene perfil, asi que dejarlo
// vacio deja a la persona mirando una pagina en blanco.
const PERFILES = [
  { valor: 'comercial', etiqueta: 'Comercial' },
  { valor: 'soporte', etiqueta: 'Soporte' },
  { valor: 'qa', etiqueta: 'QA' },
  { valor: 'administracion', etiqueta: 'Administración' },
  { valor: 'desarrollo', etiqueta: 'Desarrollo' },
];

export default function Users({ user }) {
  const [users, setUsers] = useState([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Create user dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const createDataVacio = {
    name: '',
    email: '',
    password: '',
    role: 'user',
    profile: 'none',
    permissions: permisosCon(false),
    registra_jornada: false,
    jornada_semanal_horas: 40,
    jornada_parcial: false,
    nif: '',
  };
  const [createData, setCreateData] = useState(createDataVacio);

  // Trabajador y representante solo entran al registro de jornada: el backend
  // les cierra todo lo demás (fichajes_candado.py), así que perfil y permisos
  // no les sirven de nada y no se piden.
  const soloFicha = (rol) => rol === 'trabajador' || rol === 'labor_rep';

  const handleCreateUser = async () => {
    if (!createData.name.trim() || !createData.email.trim() || !createData.password) {
      toast.error('Nombre, email y contraseña son obligatorios');
      return;
    }
    if (createData.password.length < 8) {
      toast.error('La contraseña debe tener al menos 8 caracteres');
      return;
    }
    // Un usuario normal sin perfil ni permisos entra a una aplicación vacía:
    // el menú se queda en Dashboard/Fichaje y el Dashboard no pinta nada.
    if (createData.role !== 'admin' && !soloFicha(createData.role)) {
      if (createData.profile === 'none') {
        toast.error('Elige un perfil: sin él, el usuario entra a un panel vacío');
        return;
      }
      if (!Object.values(createData.permissions).some(Boolean)) {
        toast.error('Marca al menos un permiso: sin ninguno no verá ningún módulo');
        return;
      }
    }
    setCreating(true);
    try {
      // El email se normaliza también aquí para que el aviso de duplicado
      // salte antes de crear un segundo usuario que solo cambia en mayúsculas.
      await axiosInstance.post('/auth/register', {
        ...createData,
        email: createData.email.trim().toLowerCase(),
        profile: createData.profile === 'none' ? null : createData.profile,
        // Un trabajador registra jornada siempre: es para lo único que existe.
        registra_jornada: createData.role === 'trabajador' || createData.registra_jornada,
        jornada_semanal_horas: Number(createData.jornada_semanal_horas) || 40,
        nif: createData.nif.trim() || null,
      });
      toast.success(
        `Usuario "${createData.name}" creado. Al entrar tendrá que elegir su propia contraseña.`
      );
      setCreateDialogOpen(false);
      setCreateData(createDataVacio);
      fetchUsers();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || 'Error al crear el usuario');
    } finally {
      setCreating(false);
    }
  };
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    role: 'user',
    profile: null,
    permissions: permisosCon(false),
    registra_jornada: false,
    jornada_semanal_horas: 40,
    jornada_parcial: false,
    nif: '',
    email_personal: '',
    is_active: true,
  });

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get('/users');
      setUsers(response.data);
    } catch (error) {
      toast.error('Error al cargar usuarios');
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handlePermissionChange = async (userId, permission, value) => {
    const targetUser = users.find(u => u.id === userId);
    const updatedPermissions = {
      ...targetUser.permissions,
      [permission]: value
    };

    try {
      // El backend espera {permissions: {...}}, no los permisos sueltos. Como
      // el modelo ignora los campos que no conoce, mandarlos planos se
      // descartaba en silencio y la respuesta era "No fields to update".
      await axiosInstance.put(`/users/${userId}`, { permissions: updatedPermissions });
      toast.success('Permisos actualizados');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar permisos');
    }
  };

  const handleOpenEdit = (userToEdit) => {
    setEditingUser(userToEdit);
    setFormData({
      name: userToEdit.name,
      email: userToEdit.email,
      phone: userToEdit.phone || '',
      password: '',
      confirmPassword: '',
      role: userToEdit.role || 'user',
      profile: userToEdit.profile || null,
      permissions: permisosDe(userToEdit),
      registra_jornada: !!userToEdit.registra_jornada,
      jornada_semanal_horas: userToEdit.jornada_semanal_horas ?? 40,
      jornada_parcial: !!userToEdit.jornada_parcial,
      nif: userToEdit.nif || '',
      email_personal: userToEdit.email_personal || '',
      is_active: userToEdit.is_active !== false,
    });
    setShowPassword(false);
    setShowConfirmPassword(false);
    setEditDialogOpen(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();

    // Validar contraseñas si se está cambiando
    if (formData.password || formData.confirmPassword) {
      if (formData.password !== formData.confirmPassword) {
        toast.error('Las contraseñas no coinciden');
        return;
      }
      if (formData.password.length < 6) {
        toast.error('La contraseña debe tener al menos 6 caracteres');
        return;
      }
    }

    try {
      const updateData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        role: formData.role,
        profile: formData.profile === "none" ? null : formData.profile,
        permissions: formData.permissions,
        registra_jornada: formData.role === 'trabajador' || formData.registra_jornada,
        jornada_semanal_horas: Number(formData.jornada_semanal_horas) || 40,
        jornada_parcial: formData.jornada_parcial,
        // Cadena vacía = quitarlo (el backend la guarda como null).
        nif: formData.nif,
        email_personal: formData.email_personal,
        is_active: formData.is_active,
      };

      // Solo incluir password si se proporcionó uno nuevo
      if (formData.password) {
        updateData.password = formData.password;
      }

      await axiosInstance.put(`/users/${editingUser.id}`, updateData);
      toast.success('Usuario actualizado correctamente');
      setEditDialogOpen(false);
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar usuario');
    }
  };

  // Se derivan de PERMISOS para que no vuelvan a desincronizarse: la version
  // anterior de "Todas" se dejaba fuera plantillas_email sin que se notara.
  const handleSelectAllPermissions = () => {
    setFormData({ ...formData, permissions: permisosCon(true) });
  };

  const handleSelectNoPermissions = () => {
    setFormData({ ...formData, permissions: permisosCon(false) });
  };

  const handlePermissionToggle = (permission) => {
    setFormData({
      ...formData,
      permissions: {
        ...formData.permissions,
        [permission]: !formData.permissions[permission]
      }
    });
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!(await confirmAsync(`¿Estás seguro de que quieres eliminar al usuario "${userName}"? Esta acción no se puede deshacer.`))) {
      return;
    }
    
    try {
      await axiosInstance.delete(`/users/${userId}`);
      toast.success('Usuario eliminado correctamente');
      fetchUsers();
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Error al eliminar usuario');
      }
    }
  };

  return (
    <Layout user={user}>
      <div className="space-y-6" data-testid="users-container">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2" data-testid="users-title">Gestión de Usuarios</h1>
            <p className="text-slate-600">Administra permisos de acceso a módulos</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => setCreateDialogOpen(true)}
              className="flex items-center gap-2"
              data-testid="btn-create-user"
            >
              <UsersIcon className="w-4 h-4" />
              Crear usuario
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4">
          {users.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <UsersIcon className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-600">No hay usuarios registrados.</p>
              </CardContent>
            </Card>
          ) : (
            users.map((u) => (
              <Card key={u.id} className="shadow-lg" data-testid={`user-card-${u.id}`}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="flex items-center gap-2 flex-wrap">
                        {u.name}
                        {u.role === 'admin' && (
                          <Badge className="bg-purple-100 text-purple-800 flex items-center gap-1">
                            <Shield className="w-3 h-3" />
                            Admin
                          </Badge>
                        )}
                        {u.role === 'labor_rep' && (
                          <Badge className="bg-blue-100 text-blue-800 flex items-center gap-1">
                            ⚖️ Rep. Legal
                          </Badge>
                        )}
                        {u.role === 'trabajador' && (
                          <Badge className="bg-slate-100 text-slate-800 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Trabajador
                          </Badge>
                        )}
                        {u.is_active === false && (
                          <Badge className="bg-red-50 text-red-700">Desactivado</Badge>
                        )}
                        {u.profile === 'comercial' && (
                          <Badge className="bg-purple-50 text-purple-700 flex items-center gap-1">
                            💼 Comercial
                          </Badge>
                        )}
                        {u.profile === 'soporte' && (
                          <Badge className="bg-orange-50 text-orange-700 flex items-center gap-1">
                            🎫 Soporte
                          </Badge>
                        )}
                        {u.profile === 'qa' && (
                          <Badge className="bg-blue-50 text-blue-700 flex items-center gap-1">
                            🔍 QA
                          </Badge>
                        )}
                        {u.profile === 'administracion' && (
                          <Badge className="bg-green-50 text-green-700 flex items-center gap-1">
                            📊 Admin
                          </Badge>
                        )}
                        {u.profile === 'desarrollo' && (
                          <Badge className="bg-indigo-50 text-indigo-700 flex items-center gap-1">
                            💻 Dev
                          </Badge>
                        )}
                      </CardTitle>
                      <p className="text-sm text-slate-600 mt-1" data-testid="user-email">{u.email}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleOpenEdit(u)}
                        className="flex items-center gap-2"
                      >
                        <Edit className="w-4 h-4" />
                        Editar
                      </Button>
                      {u.id !== user.id && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteUser(u.id, u.name)}
                          className="flex items-center gap-2 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                        >
                          <Trash2 className="w-4 h-4" />
                          Eliminar
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {u.role === 'admin' ? (
                    <p className="text-sm text-slate-600">Los administradores tienen acceso completo a todos los módulos</p>
                  ) : u.role === 'labor_rep' ? (
                    <p className="text-sm text-slate-600">Representante de los trabajadores: ficha y consulta el registro de jornada del equipo, sin poder corregirlo</p>
                  ) : u.role === 'trabajador' ? (
                    <p className="text-sm text-slate-600">
                      Solo ficha: entra al registro de jornada y a nada más
                      {u.jornada_semanal_horas ? ` · ${u.jornada_semanal_horas} h/semana` : ''}
                      {u.jornada_parcial ? ' (tiempo parcial)' : ''}
                    </p>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <Label htmlFor={`${u.id}-facturacion`} className="cursor-pointer">
                          Acceso a Facturación
                        </Label>
                        <Switch
                          id={`${u.id}-facturacion`}
                          checked={u.permissions?.facturacion || false}
                          onCheckedChange={(value) => handlePermissionChange(u.id, 'facturacion', value)}
                          data-testid="permission-facturacion"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label htmlFor={`${u.id}-crm`} className="cursor-pointer">
                          Acceso a CRM
                        </Label>
                        <Switch
                          id={`${u.id}-crm`}
                          checked={u.permissions?.crm || false}
                          onCheckedChange={(value) => handlePermissionChange(u.id, 'crm', value)}
                          data-testid="permission-crm"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label htmlFor={`${u.id}-ticketing`} className="cursor-pointer">
                          Acceso a Ticketing
                        </Label>
                        <Switch
                          id={`${u.id}-ticketing`}
                          checked={u.permissions?.ticketing || false}
                          onCheckedChange={(value) => handlePermissionChange(u.id, 'ticketing', value)}
                          data-testid="permission-ticketing"
                        />
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Diálogo de Edición */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-lg sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Editar Usuario</DialogTitle>
              <DialogDescription>
                Modifica la información del usuario, rol y permisos de acceso.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpdateUser} className="space-y-4">
              <div>
                <Label htmlFor="edit-name">Nombre</Label>
                <Input
                  id="edit-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="edit-phone">Teléfono (WhatsApp)</Label>
                <Input
                  id="edit-phone"
                  type="tel"
                  placeholder="+34 600 000 000"
                  value={formData.phone || ''}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
                <p className="text-xs text-gray-500 mt-1">Incluye el código de país (ej: +34 para España)</p>
              </div>

              {/* Selector de Rol */}
              <div className="space-y-2">
                <Label htmlFor="edit-role" className="text-base font-semibold">Rol del Usuario</Label>
                <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                  <SelectTrigger id="edit-role" className="h-12">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">
                      🛡️ Administrador - Acceso completo
                    </SelectItem>
                    <SelectItem value="user">
                      👤 Usuario - Permisos personalizados
                    </SelectItem>
                    <SelectItem value="trabajador">
                      🕒 Trabajador - Solo ficha
                    </SelectItem>
                    <SelectItem value="labor_rep">
                      ⚖️ Representante de los trabajadores - Ficha y consulta el registro
                    </SelectItem>
                  </SelectContent>
                </Select>
                {formData.role === 'admin' && (
                  <p className="text-xs text-green-600 font-medium">
                    ✓ Acceso completo a todos los módulos
                  </p>
                )}
                {formData.role === 'trabajador' && (
                  <p className="text-xs text-slate-600 font-medium">
                    Solo ve «Fichar» y «Mis registros». El resto de la aplicación le queda cerrado.
                  </p>
                )}
                {formData.role === 'labor_rep' && (
                  <p className="text-xs text-blue-600 font-medium">
                    ⚖️ Ficha y consulta el registro de jornada de toda la plantilla, sin poder corregirlo
                  </p>
                )}
              </div>

              {/* Selector de Perfil - Para personalizar Dashboard. Quien solo
                  ficha no tiene panel. */}
              {!soloFicha(formData.role) && (
              <div className="space-y-2">
                <Label htmlFor="edit-profile" className="text-base font-semibold">Perfil de Dashboard</Label>
                <Select value={formData.profile || "none"} onValueChange={(value) => setFormData({ ...formData, profile: value === "none" ? null : value })}>
                  <SelectTrigger id="edit-profile" className="h-12">
                    <SelectValue placeholder="Seleccionar perfil..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">
                      Sin perfil específico
                    </SelectItem>
                    <SelectItem value="comercial">
                      💼 Comercial - Ver mis acciones del día
                    </SelectItem>
                    <SelectItem value="soporte">
                      🎫 Soporte - Ver tickets urgentes
                    </SelectItem>
                    <SelectItem value="qa">
                      🔍 QA/Calidad - Ver tickets de QA
                    </SelectItem>
                    <SelectItem value="administracion">
                      📊 Administración - Ver facturas pendientes
                    </SelectItem>
                    <SelectItem value="desarrollo">
                      💻 Desarrollo - Ver tickets de desarrollo
                    </SelectItem>
                  </SelectContent>
                </Select>
                {formData.profile === 'comercial' && (
                  <p className="text-xs text-purple-600 font-medium">
                    📅 Dashboard mostrará: Mis Acciones de Hoy
                  </p>
                )}
                {formData.profile === 'soporte' && (
                  <p className="text-xs text-orange-600 font-medium">
                    🎫 Dashboard mostrará: Tickets Urgentes de Hoy
                  </p>
                )}
                {formData.profile === 'qa' && (
                  <p className="text-xs text-blue-600 font-medium">
                    🔍 Dashboard mostrará: Tickets del Departamento de QA
                  </p>
                )}
                {formData.profile === 'administracion' && (
                  <p className="text-xs text-green-600 font-medium">
                    📊 Dashboard mostrará: Facturas Pendientes
                  </p>
                )}
                {formData.profile === 'desarrollo' && (
                  <p className="text-xs text-indigo-600 font-medium">
                    💻 Dashboard mostrará: Tickets del Departamento de Desarrollo
                  </p>
                )}
              </div>
              )}

              {/* Permisos de Módulos - Solo para usuarios normales */}
              {formData.role !== 'admin' && !soloFicha(formData.role) && (
                <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50 space-y-4">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                    <Label className="text-base font-semibold text-gray-900">Permisos del Menú</Label>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={handleSelectAllPermissions}
                        className="flex-1 sm:flex-none bg-white"
                      >
                        ✓ Todas
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={handleSelectNoPermissions}
                        className="flex-1 sm:flex-none bg-white"
                      >
                        ✗ Ninguna
                      </Button>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    {PERMISOS.map(({ clave, icono, etiqueta, abre }) => (
                      <div
                        key={clave}
                        className="flex items-center justify-between p-3 bg-white border-2 border-gray-200 rounded-lg hover:border-blue-300 transition-colors"
                      >
                        <div className="flex-1 pr-4">
                          <Label htmlFor={`perm-${clave}`} className="cursor-pointer font-semibold text-sm">
                            {icono} {etiqueta}
                          </Label>
                          <p className="text-xs text-gray-500 mt-0.5">{abre}</p>
                        </div>
                        <Switch
                          id={`perm-${clave}`}
                          checked={formData.permissions[clave]}
                          onCheckedChange={() => handlePermissionToggle(clave)}
                          className="ml-4 data-[state=checked]:bg-green-600"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="edit-password">Nueva Contraseña (opcional)</Label>
                <div className="relative">
                  <Input
                    id="edit-password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Dejar en blanco para no cambiar"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Mínimo 6 caracteres</p>
              </div>
              <div>
                <Label htmlFor="edit-confirm-password">Confirmar Nueva Contraseña</Label>
                <div className="relative">
                  <Input
                    id="edit-confirm-password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    placeholder="Repetir contraseña"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              {/* Registro de jornada (routers/fichajes.py) */}
              <Card className="mt-6">
                <CardHeader>
                  <CardTitle className="text-lg">Registro de jornada</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {formData.role !== 'trabajador' && (
                    <div className="flex items-center justify-between">
                      <div className="pr-4">
                        <Label htmlFor="edit-registra" className="font-semibold">Registra jornada</Label>
                        <p className="text-xs text-gray-500">Tiene que fichar: sale en «Equipo» y recibe su resumen cada mes.</p>
                      </div>
                      <Switch
                        id="edit-registra"
                        checked={formData.registra_jornada}
                        onCheckedChange={(v) => setFormData({ ...formData, registra_jornada: v })}
                      />
                    </div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="edit-jornada">Jornada pactada (horas/semana)</Label>
                      <Input
                        id="edit-jornada"
                        type="number"
                        min="1"
                        max="60"
                        step="0.5"
                        value={formData.jornada_semanal_horas}
                        onChange={(e) => setFormData({ ...formData, jornada_semanal_horas: e.target.value })}
                      />
                    </div>
                    <div className="flex items-end justify-between gap-4 pb-2">
                      <Label htmlFor="edit-parcial">Tiempo parcial</Label>
                      <Switch
                        id="edit-parcial"
                        checked={formData.jornada_parcial}
                        onCheckedChange={(v) => setFormData({ ...formData, jornada_parcial: v })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-nif">NIF</Label>
                      <Input
                        id="edit-nif"
                        value={formData.nif}
                        onChange={(e) => setFormData({ ...formData, nif: e.target.value.toUpperCase() })}
                        placeholder="12345678Z"
                      />
                      <p className="text-xs text-gray-500 mt-1">Identifica a la persona en la exportación para Inspección.</p>
                    </div>
                    <div>
                      <Label htmlFor="edit-email-personal">Email personal (opcional)</Label>
                      <Input
                        id="edit-email-personal"
                        type="email"
                        value={formData.email_personal}
                        onChange={(e) => setFormData({ ...formData, email_personal: e.target.value })}
                        placeholder="nombre@gmail.com"
                      />
                      <p className="text-xs text-gray-500 mt-1">Su resumen mensual le llega también aquí, aunque deje la empresa.</p>
                    </div>
                  </div>
                  {editingUser?.id !== user?.id && (
                    <div className="flex items-center justify-between border-t pt-4">
                      <div className="pr-4">
                        <Label htmlFor="edit-activo" className="font-semibold">Cuenta activa</Label>
                        <p className="text-xs text-gray-500">
                          Desactivar en vez de borrar: su registro se conserva 4 años y, al
                          desactivarla, recibe por email su registro del mes.
                        </p>
                      </div>
                      <Switch
                        id="edit-activo"
                        checked={formData.is_active}
                        onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
                      />
                    </div>
                  )}
                </CardContent>
              </Card>

              <div className="flex gap-2 justify-end pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setEditDialogOpen(false)}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="bg-[#9C0F17] hover:bg-[#7A0C12]"
                >
                  Guardar Cambios
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Create user dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Crear usuario</DialogTitle>
              <DialogDescription>
                Se creará dentro de tu empresa. La contraseña que pongas solo vale
                para el primer acceso: al entrar tendrá que elegir la suya.
              </DialogDescription>
            </DialogHeader>
            <form
              onSubmit={(e) => { e.preventDefault(); handleCreateUser(); }}
              className="space-y-4"
            >
              <div>
                <Label htmlFor="create-name">Nombre *</Label>
                <Input
                  id="create-name"
                  value={createData.name}
                  onChange={(e) => setCreateData({ ...createData, name: e.target.value })}
                  autoFocus
                />
              </div>
              <div>
                <Label htmlFor="create-email">Email *</Label>
                <Input
                  id="create-email"
                  type="email"
                  value={createData.email}
                  onChange={(e) => setCreateData({ ...createData, email: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="create-password">Contraseña * (mín. 8 caracteres)</Label>
                <Input
                  id="create-password"
                  type="password"
                  value={createData.password}
                  onChange={(e) => setCreateData({ ...createData, password: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="create-role">Rol</Label>
                <Select
                  value={createData.role}
                  onValueChange={(v) => setCreateData({ ...createData, role: v })}
                >
                  <SelectTrigger id="create-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">Usuario</SelectItem>
                    <SelectItem value="trabajador">Trabajador (solo ficha)</SelectItem>
                    <SelectItem value="admin">Administrador</SelectItem>
                    <SelectItem value="labor_rep">Representante de los trabajadores</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Un administrador lo ve todo por rol: perfil y permisos no le
                  afectan, asi que no se piden. */}
              {(soloFicha(createData.role) || createData.registra_jornada) && (
                <div className="grid grid-cols-2 gap-3 border rounded-lg p-3">
                  <div>
                    <Label htmlFor="create-nif">NIF</Label>
                    <Input
                      id="create-nif"
                      value={createData.nif}
                      onChange={(e) => setCreateData({ ...createData, nif: e.target.value.toUpperCase() })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="create-jornada">Horas/semana</Label>
                    <Input
                      id="create-jornada"
                      type="number"
                      min="1"
                      max="60"
                      step="0.5"
                      value={createData.jornada_semanal_horas}
                      onChange={(e) => setCreateData({ ...createData, jornada_semanal_horas: e.target.value })}
                    />
                  </div>
                  <div className="col-span-2 flex items-center justify-between">
                    <Label htmlFor="create-parcial">Tiempo parcial</Label>
                    <Switch
                      id="create-parcial"
                      checked={createData.jornada_parcial}
                      onCheckedChange={(v) => setCreateData({ ...createData, jornada_parcial: v })}
                    />
                  </div>
                </div>
              )}

              {createData.role !== 'trabajador' && (
                <div className="flex items-center justify-between">
                  <Label htmlFor="create-registra">Registra jornada (tiene que fichar)</Label>
                  <Switch
                    id="create-registra"
                    checked={createData.registra_jornada}
                    onCheckedChange={(v) => setCreateData({ ...createData, registra_jornada: v })}
                  />
                </div>
              )}

              {createData.role !== 'admin' && !soloFicha(createData.role) && (
                <>
                  <div>
                    <Label htmlFor="create-profile">Perfil *</Label>
                    <Select
                      value={createData.profile}
                      onValueChange={(v) => setCreateData({ ...createData, profile: v })}
                    >
                      <SelectTrigger id="create-profile">
                        <SelectValue placeholder="Elige un perfil" />
                      </SelectTrigger>
                      <SelectContent>
                        {PERFILES.map(({ valor, etiqueta }) => (
                          <SelectItem key={valor} value={valor}>{etiqueta}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500 mt-1">
                      Decide qué información ve en su panel de inicio.
                    </p>
                  </div>

                  <div>
                    <Label>Permisos *</Label>
                    <p className="text-xs text-gray-500 mt-1 mb-2">
                      Sin ningún permiso marcado no verá ningún módulo en el menú.
                    </p>
                    <div className="space-y-2">
                      {PERMISOS.map(({ clave, icono, etiqueta, abre }) => (
                        <div
                          key={clave}
                          className="flex items-center justify-between p-3 bg-white border-2 border-gray-200 rounded-lg hover:border-blue-300 transition-colors"
                        >
                          <div className="flex-1 pr-4">
                            <Label
                              htmlFor={`create-perm-${clave}`}
                              className="cursor-pointer font-semibold text-sm"
                            >
                              {icono} {etiqueta}
                            </Label>
                            <p className="text-xs text-gray-500 mt-0.5">{abre}</p>
                          </div>
                          <Switch
                            id={`create-perm-${clave}`}
                            checked={createData.permissions[clave]}
                            onCheckedChange={(valor) => setCreateData({
                              ...createData,
                              permissions: { ...createData.permissions, [clave]: valor },
                            })}
                            className="ml-4 data-[state=checked]:bg-green-600"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setCreateDialogOpen(false)}
                  disabled={creating}
                >
                  Cancelar
                </Button>
                <Button type="submit" disabled={creating}>
                  {creating ? 'Creando...' : 'Crear usuario'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
